import { memo, useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import './containers/mainSections/mainSections.css';
import SideNav, { userSections, adminSections } from './containers/sideNav';
import { checkReportStatus, deleteReport, fetchApiDeleteSchedule, fetchApiGetSchedules, fetchApiGetUsers, fetchApiScheduleRegister, fetchApiUpdateSchedule, fetchApiUpdateUserData, fetchApiUserDelete, fetchApiUserRegister, fetchApiUserSchedulesGroups, fetchApiUserSchedules, fetchApiUserUpdate, fetchGenerateReport, fetchGeofences, fetchGeoTags, fetchOrientationReport, fetchRetrieveReport, fetchStatusColorUpdate, fetchUnreadUserEventsList, fetchUserDevices, fetchUserEvents, fetchUserInfo, fetchUserLogOut, fetchApiSchedulesGroupsRegister, fetchApiSchedulesGroupsDelete, fetchApiSchedulesGroupsUpdate } from './utils/requests';
import { Alert, AlertTitle } from '@mui/material';
import Reports from './containers/mainSections/reports';
import useWebSocket from './utils/webSocket';
import Footer from './containers/footer';
import Users from './containers/mainSections/adminSections/users';
import loaderAnimated from './assets/images/dashboard/loader_animated.gif';
import ExpirationModal from './components/expirationModal';
import Schedules from './containers/mainSections/adminSections/schedules';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Login from './containers/login';
import Administration from './containers/mainSections/administration';
import Tracking from './containers/mainSections/tracking';
import Header from './containers/header';
import BlockModal from './components/blockModal';
import Settings from './containers/mainSections/settings';
import { parseOrientationCSV, timeToMinutes, timeToSeconds } from './utils/generalFunctions';

const DEFAULT_COMPENSATION_TIME = { time: '00:00', type: 'late' };

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userHash, setUserHash] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
  const [showMovilMenu, setShowMovilMenu] = useState(false);

  const [userInfo, setUserInfo] = useState([]);
  const [apiUserInfo, setApiUserInfo] = useState({});
  const [accountVigent, setAccountVigent] = useState(true);
  const [isOpenExpModal, setIsOpenExpModal] = useState(false);
  const [isOpenBlockModal, setIsOpenBlockModal] = useState(false);

  const [allUsers, setAllUsers] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]);

  const sections = apiUserInfo && apiUserInfo.type === 'admin' ? adminSections : userSections;
  const [activeSection, setActiveSection] = useState(sections[0][0]?.label || '');

  const [geoTags, setGeoTags] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [devices, setDevices] = useState([]);
  const [deviceOrientations, setDeviceOrientations] = useState({});
  const [userGeoTag, setUserGeoTag] = useState('Checadas');
  const [userRoute, setUserRoute] = useState([]);
  const [userSchedules, setUserSchedules] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [defaultEventRows, setDefaultEventRows] = useState([]);
  const [rows, setRows] = useState([]);

  const [alerts, setAlerts] = useState([]);

  const [schedulesGroups, setSchedulesGroups] = useState([]);

  const toleranceTime = apiUserInfo && apiUserInfo.toleranceTime ? apiUserInfo.toleranceTime : 30;
  const periodicGroups = apiUserInfo && apiUserInfo.periodicGroups ? apiUserInfo.periodicGroups : false;
  const compensationMode = apiUserInfo && apiUserInfo.compensationMode ? apiUserInfo.compensationMode : false;
  const compensationTime = apiUserInfo && apiUserInfo.compensationTime ? apiUserInfo.compensationTime : DEFAULT_COMPENSATION_TIME;

  const lastExecutedGroupRef = useRef({ groupId: null, startTime: null });
  const periodicIntervalRef = useRef(null);

  useEffect(() => {
    if (apiUserInfo && apiUserInfo.type !== 'admin') {
      const { status, expirationDate } = apiUserInfo;

      // Convertir "DD/MM/YYYY" a formato Date en JavaScript
      if (expirationDate) {
        const [day, month, year] = expirationDate.split("/").map(Number);
        const expiration = new Date(year, month - 1, day, 23, 59, 59); // Meses en JS van de 0 a 11

        if (status !== 'activo' || expiration < new Date()) {
          setCookie('session_key', '', 1);
          setUserHash(null);
          setAccountVigent(false);
          updateValidity();
          setIsAuthenticated(false);
          setIsOpenExpModal(true);
        }
      }
    }
  }, [apiUserInfo]);

  const applyGroupAutomatically = (group) => {
    const flat = group.schedules?.flat() || [];
    if (!flat.length) return;

    const updatedAllSchedules = userSchedules.map(schedule => {
      const match = flat.find(s => s.scheduleId === schedule._id);

      if (!match) return schedule;

      return { ...schedule, vehicleId: match.vehicleId || null };
    });

    const schedulesToUpdate = updatedAllSchedules.filter(schedule => {
      const original = userSchedules.find(s => s._id === schedule._id);

      return original?.vehicleId !== schedule.vehicleId;
    });

    if (schedulesToUpdate.length > 0) {
      updateSchedule(updatedAllSchedules, schedulesToUpdate);
    }

    lastExecutedGroupRef.current = {
      groupId: group._id,
      startTime: group.startTime
    };
  };

  const evaluateAndApplyGroup = () => {
    if (!periodicGroups) return;
    if (!schedulesGroups?.length) return;
    if (!userSchedules?.length) return;

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const validGroups = schedulesGroups
      .filter(g => g.enabled && g.startTime)
      .map(g => ({
        ...g,
        minutes: timeToMinutes(g.startTime)
      }))
      .filter(g => g.minutes !== null && g.minutes <= currentMinutes);

    if (!validGroups.length) return;

    // Buscar el más cercano al presente
    validGroups.sort((a, b) =>
      currentMinutes - a.minutes - (currentMinutes - b.minutes)
    );

    const groupToApply = validGroups[0];

    // Evitar ejecutar el mismo grupo dos veces
    if (
      lastExecutedGroupRef.current.groupId === groupToApply._id &&
      lastExecutedGroupRef.current.startTime === groupToApply.startTime
    ) {
      return;
    }

    applyGroupAutomatically(groupToApply);
  };

  useEffect(() => {
    if (!periodicGroups) {
      if (periodicIntervalRef.current) {
        clearInterval(periodicIntervalRef.current);
        periodicIntervalRef.current = null;
      }
      return;
    }

    const now = new Date();

    // Milisegundos restantes hasta el siguiente minuto exacto
    const msUntilNextMinute =
      (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    const timeout = setTimeout(() => {
      evaluateAndApplyGroup(); // Ejecutar justo en el minuto exacto

      periodicIntervalRef.current = setInterval(() => {
        evaluateAndApplyGroup();
      }, 60000);

    }, msUntilNextMinute);

    return () => {
      clearTimeout(timeout);
      if (periodicIntervalRef.current) {
        clearInterval(periodicIntervalRef.current);
        periodicIntervalRef.current = null;
      }
    };

  }, [periodicGroups, schedulesGroups]);

  useEffect(() => {
    if (!periodicGroups) return;

    // Resetear grupo ejecutado
    lastExecutedGroupRef.current = {
      groupId: null,
      startTime: null
    };

    // Evaluar inmediatamente con la nueva configuración
    evaluateAndApplyGroup();
  }, [schedulesGroups]);

  const updateValidity = async () => {
    await fetchApiUserUpdate(userHash, { status: 'inactivo' }).catch(error => console.log(error));
  }

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const setCookie = (name, value, hours) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (hours * 60 * 60 * 1000)); // Convertir horas a milisegundos
    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/;`;
  };

  useEffect(() => {
    // Aplicar clase de modo oscuro a `body`
    document.body.classList.toggle('dark-mode-variables', isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    let hash = urlParams.get('session_key');
    if (!hash) {
      hash = getCookie('session_key');
    }
    if (hash) {
      setUserHash(hash);
      setIsAuthenticated(true);
      setCookie('session_key', hash, 1);
    }
    setIsLoading(false);
  }, [location.search]);

  const handleLoginSuccess = (hash) => {
    setUserHash(hash);
    setIsAuthenticated(true);
    navigate('/seguimiento', { replace: true }); // Redirige al home principal
  };

  const handleLogout = async () => {
    try {
      await fetchUserLogOut(userHash);
      setIsLoading(false);
      setUserHash(null);
      setIsAuthenticated(false);
      setActiveSection('Login'); // Redirige a Login al cerrar sesión

      // Cancelar el temporizador al cerrar sesión
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  // Función para actualizar la URL y la sección activa
  const handleSectionChange = (sectionLabel) => {
    setActiveSection(sectionLabel);
    navigate(`/${sectionLabel.toLowerCase()}`);
  };

  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />
    }
    return children;
  }

  // Cargar la sección activa desde la URL al montar el componente
  // useEffect(() => {
  //   const path = location.pathname.slice(1).toLowerCase();
  //   const allSections = sections.flat();

  //   if (!isAuthenticated) {
  //     if (path !== 'login') navigate('/login', { replace: true });
  //     setActiveSection('Login');
  //   } else {
  //     const foundSection = allSections.find(sec => sec.label.toLowerCase() === path);
  //     if (foundSection) {
  //       setActiveSection(foundSection.label);
  //     } else {
  //       navigate('/seguimiento', { replace: true });
  //     }
  //   }
  // }, [isAuthenticated, location.pathname, sections, navigate]);

  const [locations, setLocations] = useState([]);

  const devicesRef = useRef([]);

  useEffect(() => {
    devicesRef.current = devices;
  }, [devices]);

  const updateLocations = useCallback((data) => {
    
    if (!data || !Array.isArray(data)) return;

    // Actualizar locations
    setLocations((prevLocations) => {

      const locationsMap = new Map(
        prevLocations.map((loc) => [loc.id, loc])
      );

      data.forEach((item) => {

        locationsMap.set(item.state.source_id, {
          id: item.state.source_id,
          gps: item.state.gps,
          status: item.state.movement_status,
          movementStatusUpdate: item.state.movement_status_update,
          speed: item.state.gps?.speed || 0,
          connectionStatus: item.state.connection_status,
        });
      });

      return Array.from(locationsMap.values());
    });
    
    // Actualizar orientations en devices
    setDeviceOrientations((prev) => {

      const updated = { ...prev };
      data.forEach((item) => {
        // Buscar device relacionado
        const device = devicesRef.current.find(
          d => d.source?.id === item.state.source_id
        );

        if (!device) {
          return;
        }

        const gps = item.state.gps;

        if (
          !gps?.updated ||
          gps?.heading == null
        ) {
          return;
        }

        const newOrientation = {
          time: gps.updated,
          heading: gps.heading,
          seconds: timeToSeconds(
            gps.updated.split(' ')[1]
          )
        };
        const current = updated[device.id] || [];

        // Evitar duplicados
        const alreadyExists = current.some(
          o => o.time === newOrientation.time
        );

        if (alreadyExists) return;

        updated[device.id] = [
          ...current,
          newOrientation
        ]
          .sort((a, b) => b.seconds - a.seconds)
          .slice(0, 15);
      });

      return updated;
    });
  }, []);

  useWebSocket(userHash, updateLocations, apiUserInfo);

  useEffect(() => {
    if (!isAuthenticated || userHash === null) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const userInfo = await fetchUserInfo(userHash);
        setUserInfo(userInfo);
      } catch (error) {
        // console.error('Error al obtener los datos del usuario:', error);
        setCookie('session_key', '', 1);
        setUserHash(null);
        setIsAuthenticated(false);
        showAlert("Error al obtener los datos del usuario.", "error");
        setIsLoading(false);
        return;
      }

      // try {

      //   setGeoTags(dataGeoTags.list);
      // } catch (error) {
      //   console.error('Error al obtener los datos de las etiquetas:', error);
      // }

      // try {

      //   setGeofences(dataGeofences.list);
      // } catch (error) {
      //   console.error('Error al obtener los datos de las geocercas:', error);
      // }

      try {
        // const dataGeoTags = await fetchGeoTags(userHash);
        // const dataGeofences = await fetchGeofences(userHash);
        // const dataDevices = await fetchUserDevices(userHash);

        const [dataGeoTags, dataGeofences, dataDevices] = await Promise.all([
          fetchGeoTags(userHash),
          fetchGeofences(userHash),
          fetchUserDevices(userHash)
        ]);

        const dataSchedules = await fetchApiUserSchedules(userHash);

        const endTime = new Date();

        const startTime = new Date();
        startTime.setMinutes(startTime.getMinutes() - 20);
        // startTime.setHours(0, 0, 0, 0);
        const dataEvents = await fetchUserEvents(userHash, ['outzone'], startTime.toISOString(), endTime.toISOString());

        if (dataEvents && dataEvents.list) {
          // console.log(dataEvents.list);
          setUserEvents(dataEvents.list);
        }

        if (dataSchedules && dataSchedules.data) setUserSchedules(dataSchedules.data);

        if (dataDevices && dataDevices.list && dataGeoTags && dataGeoTags.list && dataGeofences && dataGeofences.list) {
          // setDevices(dataDevices.list);
          setGeoTags(dataGeoTags.list);
          setGeofences(dataGeofences.list);

          try {
            const dataUser = await fetchApiUpdateUserData(userHash, dataDevices.list, dataGeoTags.list, dataGeofences.list);

            setApiUserInfo(dataUser.data);

            if (dataUser.data.type === 'admin') {
              try {
                const users = await fetchApiGetUsers(userHash);
                setAllUsers(users.data);
              } catch (error) {
                showAlert("Error al obtener los datos de los usuarios.", "error");
              }

              try {
                const schedules = await fetchApiGetSchedules(userHash);
                setAllSchedules(schedules.data);
              } catch (error) {
                showAlert("Error al obtener los datos de los usuarios.", "error");
              }

              return;
            }

            setUserGeoTag(dataUser.data.geoTag);
            setUserRoute(dataUser.data.route);

            // Periodo de 10 minutos previos
            const orientationEnd = new Date();
            const orientationStart = new Date();
            orientationStart.setMinutes(orientationStart.getMinutes() - 10);

            const from = orientationStart.toISOString();
            const to = orientationEnd.toISOString();

            // Hacer petición por cada vehículo
            const availableDevices = dataUser.data.type === 'admin' ? dataDevices.list : dataDevices.list.filter(device => {
              const userVehicle = dataUser.data.vehicles.find(v => v.id === device.id);
              return userVehicle?.billing === true;
            });

            const devicesWithOrientation = await Promise.all(
              availableDevices.map(async (device) => {
                try {
                  const csvText = await fetchOrientationReport(
                    userHash,
                    from,
                    to,
                    device.id
                  );

                  const parsedData = parseOrientationCSV(csvText);

                  return {
                    ...device,
                    status: dataUser.data.vehicles.find(v => v.id === device.id)?.status,
                    schedule: device.schedule ?? '',
                    orientations: parsedData
                  };
                } catch (error) {
                  return {
                    ...device,
                    status: dataUser.data.vehicles.find(v => v.id === device.id)?.status,
                    schedule: device.schedule ?? '',
                    orientations: []
                  };
                }
              })
            );

            const orientationsMap = {};

            devicesWithOrientation.forEach(device => {

              orientationsMap[device.id] =
                (device.orientations || [])
                  .map(o => ({
                    ...o,
                    seconds: timeToSeconds(o.time.split(' ')[1])
                  }))
                  .sort((a, b) => b.seconds - a.seconds)
                  .slice(0, 15);

            });

            setDeviceOrientations(orientationsMap);

            // setDevices(devicesWithOrientation);
            setDevices(prevDevices => {

              return devicesWithOrientation.map(newDevice => {
                const previous = prevDevices.find(d => d.id === newDevice.id);

                if (!previous) return newDevice;

                const merged = { ...previous };

                Object.entries(newDevice).forEach(([key, value]) => {
                  if (value !== undefined && value !== null) {
                    merged[key] = value;
                  }
                });

                return merged;
              });
            });

            try {
              const dataGroups = await fetchApiUserSchedulesGroups(userHash);

              if (dataGroups && dataGroups.data) {
                setSchedulesGroups(dataGroups.data);
              }
            } catch (error) {
              showAlert("Error al obtener los grupos de horarios.", "error");
            }

          } catch (error) {
            if (error.status && error.status === 404) {
              showAlert("Error al obtener la información del usuario.", "error");

            } else {
              // console.error('Error al obtener los vehículos desde la Api:', error);
              showAlert("Error al obtener la información de los vehículos. \nInténtelo nuevamente o más tarde.", "error");
              // const dataDevicesWithStatus = dataDevices.list.map((device) => ({
              //   ...device,
              //   status: 'inactivo',
              //   schedule: device.schedule ?? '', 
              // }));

              // setDevices(dataDevicesWithStatus);
            }
            setIsOpenBlockModal(true);
            setCookie('session_key', '', 1);
            setUserHash(null);
            setIsAuthenticated(false);
            setIsLoading(false);
            return;
          }

        } else {
          // console.error('La respuesta no contiene dispositivos o tiene una estructura inesperada.', dataDevices);
          showAlert("Error al obtener los vehículos del usuario. \nInténtelo nuevamente o más tarde.", "error");
        }
      } catch (error) {
        // console.error('Error al cargar los datos:', error);
        showAlert(error.message + "\nInténtelo nuevamente o más tarde.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated, userHash]);



  const isFetchingRef = useRef(false); // Controla si ya se está ejecutando una petición
  const timeoutRef = useRef(null); // Almacenar el identificador del temporizador


  const checkUnreadEvents = async () => {
    if (isFetchingRef.current || !isAuthenticated || userHash === null || apiUserInfo.type === 'admin') return; // Evitar duplicados

    isFetchingRef.current = true; // Marcar como en ejecución
    try {
      const currentTime = new Date();
      const startTime = new Date(); // Crear una copia para el rango
      startTime.setSeconds(currentTime.getSeconds() - 30);

      //const unreadEventsCount = await fetchUnreadUserEventsCount(userHash, startTime.toISOString());

      //if (unreadEventsCount && unreadEventsCount.success && unreadEventsCount.count > 0) {

      const currentEventsData = await fetchUnreadUserEventsList(userHash, ["outzone"], startTime.toISOString());

      if (currentEventsData && currentEventsData.list && currentEventsData.list.length > 0) {
        setUserEvents((prevEvents) => {

          const newEvents = currentEventsData.list.filter(
            (event) =>
              !prevEvents.some(
                (prevEvent) =>
                  prevEvent.id === event.id &&
                  prevEvent.track_id === event.track_id &&
                  prevEvent.time === event.time
              )
          );

          return [...newEvents, ...prevEvents];
        });
      }
      //}

    } catch (error) {
      console.error("Error al obtener eventos:", error);
    } finally {
      isFetchingRef.current = false; // Marcar como terminado
      if (isAuthenticated && userHash !== null) {
        timeoutRef.current = setTimeout(checkUnreadEvents, 20000); // Programar siguiente ejecución
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated && userHash !== null && apiUserInfo.type === 'user') {
      const { status, expirationDate } = apiUserInfo;

      // Convertir "DD/MM/YYYY" a formato Date en JavaScript
      if (expirationDate) {
        const [day, month, year] = expirationDate.split("/").map(Number);
        const expiration = new Date(year, month - 1, day, 23, 59, 59); // Meses en JS van de 0 a 11

        if (status !== 'activo' || expiration < new Date()) {
          return;
        }
      }
      checkUnreadEvents(); // Inicia la verificación cuando userEvents tenga datos
    }

    return () => {
      // Limpiar el temporizador al desmontar o cuando cambie el estado de autenticación
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isAuthenticated, userHash, apiUserInfo]);

  const generateReport = async (startDate, endDate, trackersIds) => {
    const loadingAlertId = new Date().getTime();
    showAlert("Generando reporte...", "info", loadingAlertId);

    const activeZoneIds = Array.from(new Set(
      userRoute
        .filter(routePoint => routePoint.state !== 'omitido')
        .map(routePoint => routePoint.id)
        .filter(Boolean)
    ));
    const allZoneIds = Array.from(new Set(
      userRoute
        .map(routePoint => routePoint.id)
        .filter(Boolean)
    ));
    const zoneIds = activeZoneIds.length > 0 ? activeZoneIds : allZoneIds;

    const data = {
      trackers: trackersIds,
      title: 'Reporte de visitas a geocercas.',
      from: startDate,
      to: endDate,
      plugin: {
        "hide_empty_tabs": true,
        "plugin_id": 8,
        "show_seconds": true,
        "include_summary_sheet_only": false,
        "include_summary_sheet": false,
        "show_mileage": false,
        "show_not_visited_zones": false,
        "min_minutes_in_zone": 0,
        "hide_charts": true,
        "zone_ids": zoneIds
      }
    };

    try {
      const reportData = await fetchGenerateReport(userHash, data);

      if (reportData?.id) {
        // Declarar retrievedReport fuera del callback
        const retrievedReport = await new Promise((resolve, reject) => {
          checkReportStatus(
            userHash,
            reportData.id,
            async () => {
              try {
                const report = await fetchRetrieveReport(userHash, reportData.id);

                await deleteReport(userHash, reportData.id);
                resolve(report); // Resolver el valor del reporte recuperado
              } catch (error) {
                reject(error); // Manejar errores en la recuperación
              }
            }
          );
        });

        replaceAlert(loadingAlertId, "¡Reporte generado con éxito!", "success");
        return retrievedReport; // Retornar el reporte recuperado
      }

      replaceAlert(loadingAlertId, "No se pudo generar el reporte", "error");
      return null;
    } catch (error) {
      if (error.code && error.code === 211) {
        showAlert("Error al generar el reporte. El lapso de tiempo solicitado es demasiado grande.", "error");

      } else if (error.code && error.code === 236) {
        showAlert("Error al generar el reporte. Uno o más de los vehículos seleccionados no cuenta con el plan de servicios necesario para generar reportes.", "error");

      } else {
        replaceAlert(
          loadingAlertId,
          "Error al generar el reporte. Verifique que las fechas sean válidas.",
          "error"
        );
      }
      replaceAlert(loadingAlertId, "No se pudo generar el reporte", "error");
      return null;
    }
  };

  const generateOrientationsReport = async (from, to, vehicleIds) => {

    const orientationsByVehicle = await Promise.all(

      vehicleIds.map(async (vehicleId) => {

        try {

          const csvText = await fetchOrientationReport(
            userHash,
            from,
            to,
            vehicleId
          );

          const parsed = parseOrientationCSV(csvText);

          return {
            vehicleId,
            orientations: parsed
          };

        } catch (error) {

          console.error(`Error orientación ${vehicleId}`, error);

          return {
            vehicleId,
            orientations: []
          };
        }
      })

    );

    return orientationsByVehicle;
  };

  const userRegister = async (hash, expirationDate) => {
    const loadingAlertId = new Date().getTime(); // ID único para la alerta de carga
    showAlert("Actualizando información del usuario...", "info", loadingAlertId);
    try {
      const userRegistered = await fetchApiUserRegister(userHash, hash, expirationDate);

      setAllUsers(prev => ([...prev, { ...userRegistered.data }]));

      replaceAlert(loadingAlertId, "¡Usuario registrado con éxito!", "success");

      return { data: userRegistered.data, status: 201 };
    } catch (error) {
      replaceAlert(
        loadingAlertId,
        "Error al registrar el nuevo usuario: " + error.message,
        "error"
      );
      return { status: error.status, data: error.data || null };
    }
  }

  const userUpdate = async (data, isAdmin = false, showAlerts = true) => {
    const loadingAlertId = new Date().getTime(); // ID único para la alerta de carga
    if (showAlerts) {
      showAlert("Actualizando información del usuario...", "info", loadingAlertId);
    }

    try {
      const userUpdatedData = await fetchApiUserUpdate(userHash, data);

      if (isAdmin) {
        if (userUpdatedData?.data !== undefined) {
          setAllUsers(prevUsers =>
            prevUsers.map(user =>
              user.userId === data.userId ? { ...user, ...data } : user
            )
          );
        }
      } else {
        // Actualiza los datos del usuario según la respuesta
        if (userUpdatedData?.data?.geoTag !== undefined) {
          setUserGeoTag(userUpdatedData.data.geoTag);
        }
        if (userUpdatedData?.data?.route !== undefined) {
          setUserRoute(userUpdatedData.data.route);
        }
        // if (userUpdatedData?.data?.vehicles !== undefined) {
        //   setDevices(userUpdatedData.data.vehicles);
        // }
        
        if (userUpdatedData?.data?.vehicles !== undefined) {
          setDevices(prevDevices =>
            prevDevices.map(device => {
              const updated = userUpdatedData.data.vehicles.find(
                v => v.id === device.id
              );

              if (!updated) return device;

              return {
                ...device,
                ...updated
              };
            })
          );
        }

        const userInfoUpdates = {};

        if (userUpdatedData?.data?.toleranceTime !== undefined) {
          userInfoUpdates.toleranceTime = userUpdatedData.data.toleranceTime;
        }
        if (userUpdatedData?.data?.periodicGroups !== undefined) {
          userInfoUpdates.periodicGroups = userUpdatedData.data.periodicGroups;
        }
        if (userUpdatedData?.data?.compensationMode !== undefined) {
          userInfoUpdates.compensationMode = userUpdatedData.data.compensationMode;
        }
        if (userUpdatedData?.data?.compensationTime !== undefined) {
          userInfoUpdates.compensationTime = userUpdatedData.data.compensationTime;
        }

        if (Object.keys(userInfoUpdates).length > 0) {
          setApiUserInfo(prev => ({ ...prev, ...userInfoUpdates }));
        }
      }
      // Reemplaza la alerta de carga por una de éxito
      if (showAlerts) {
        replaceAlert(loadingAlertId, "¡Datos actualizados con éxito!", "success");
      }
    } catch (error) {
      if (showAlerts) {

        // Reemplaza la alerta de carga por una de error
        replaceAlert(
          loadingAlertId,
          "Error al actualizar la información del usuario: " + error.message,
          "error"
        );
      } else {
        showAlert("Error al actualizar la información del usuario.", "error");
      }
    }
  };

  const userDelete = async (data, isAdmin = false) => {
    const loadingAlertId = new Date().getTime(); // ID único para la alerta de carga
    showAlert("Eliminando usuario...", "info", loadingAlertId);

    try {
      await fetchApiUserDelete(userHash, data);

      if (isAdmin) {
        setAllUsers(prevUsers =>
          prevUsers.filter(u => u.userId !== data.userId)
        );
      }

      // Reemplaza la alerta de carga por una de éxito
      replaceAlert(loadingAlertId, "¡Usuario eliminado con éxito!", "success");
    } catch (error) {
      // Reemplaza la alerta de carga por una de error
      replaceAlert(
        loadingAlertId,
        "Error al eliminar el usuario: " + error.message,
        "error"
      );
    }
  };

  const assignControlPointIds = (data, userRoute) => {
    // Mapa para buscar por label
    const labelToId = new Map(userRoute.map(cp => [cp.label, cp.id]));

    return data.map(schedule => ({
      ...schedule,
      turns: schedule.turns.map(turn =>
        turn.map(point => {
          // Si ya tiene un id válido, respétalo
          if (point.id !== undefined && point.id !== null) {
            return point;
          }
          // Si no tiene id, asignar buscando por label
          return {
            ...point,
            id: labelToId.has(point.label) ? labelToId.get(point.label) : 0
          };
        })
      )
    }));
  };


  const scheduleRegister = async (data, isAdmin = false) => {
    const loadingAlertId = new Date().getTime();
    showAlert("Registrando horarios...", "info", loadingAlertId);

    try {
      const dataWithIds = assignControlPointIds(data, userRoute);
      const newSchedulesData = await fetchApiScheduleRegister(userHash, dataWithIds);

      if (newSchedulesData?.data?.newSchedules !== undefined) {
        const updatedSchedules = newSchedulesData.data.newSchedules;

        if (isAdmin) {
          setAllSchedules(prev => {
            // 1. Actualizar horarios existentes
            const updated = prev.map(schedule => {
              const match = updatedSchedules.find(
                newSched => newSched.userId === schedule.userId && newSched.name === schedule.name
              );
              return match ? match : schedule;
            });

            // 2. Agregar nuevos horarios que no existían en prev
            const newOnes = updatedSchedules.filter(newSched =>
              !prev.some(
                schedule => schedule.userId === newSched.userId && schedule.name === newSched.name
              )
            );

            return [...newOnes, ...updated];
          });
        } else {
          setUserSchedules(prev => {
            const updated = prev.map(schedule => {
              const match = updatedSchedules.find(
                newSched => newSched.userId === schedule.userId && newSched.name === schedule.name
              );
              return match ? match : schedule;
            });

            const newOnes = updatedSchedules.filter(newSched =>
              !prev.some(
                schedule => schedule.userId === newSched.userId && schedule.name === newSched.name
              )
            );

            return [...newOnes, ...updated];
          });
        }
      }


      replaceAlert(loadingAlertId, "¡Horarios registrados con éxito!", "success");
    } catch (error) {
      replaceAlert(
        loadingAlertId,
        "Error al registrar los horarios: " + error.message,
        "error"
      );
    }
  };

  const deleteSchedule = async (schedules, isAdmin = false) => {
    const loadingAlertId = new Date().getTime();
    showAlert("Eliminando horarios...", "info", loadingAlertId);

    try {
      const responseDeleteSchedule = await fetchApiDeleteSchedule(userHash, schedules);

      if (responseDeleteSchedule.success) {
        isAdmin ? setAllSchedules(prev =>
          prev.filter(schedule =>
            !schedules.some(s => s.userId === schedule.userId && s.name === schedule.name)
          )
        ) :
          setUserSchedules(prev => prev.filter((schedule) => !schedules.includes(schedule.name)));
      }

      replaceAlert(loadingAlertId, "¡Horarios eliminados con éxito!", "success");
    } catch (error) {
      replaceAlert(
        loadingAlertId,
        "Error al eliminar los horarios: " + error.message,
        "error"
      );
    }
  };

  const updateSchedule = async (allSchedules, data, isAdmin = false) => {
    const loadingAlertId = new Date().getTime();
    showAlert("Actualizando horarios...", "info", loadingAlertId);

    try {
      const updatedScheduleData = await fetchApiUpdateSchedule(userHash, data);

      if (updatedScheduleData?.data !== undefined) {
        isAdmin ? setAllSchedules(allSchedules) :
          setUserSchedules(allSchedules);
      }

      replaceAlert(loadingAlertId, "¡Horarios actualizados con éxito!", "success");
    } catch (error) {
      replaceAlert(
        loadingAlertId,
        "Error al actualizar los horarios: " + error.message,
        "error"
      );
    }
  };

  const updateDevicesStatusColor = async (data) => {
    // const loadingAlertId = new Date().getTime();
    // showAlert("Actualizando horarios...", "info", loadingAlertId);

    try {
      await fetchStatusColorUpdate(userHash, data);

      // replaceAlert(loadingAlertId, "¡Horarios actualizados con éxito!", "success");
    } catch (error) {
      // console.log(error);

      // replaceAlert(
      //   loadingAlertId,
      //   "Error al actualizar los estatus de los dispositivos: " + error.message,
      //   "error"
      // );
    }
  };

  const scheduleGroupRegister = async (data) => {

    const loadingAlertId = new Date().getTime();

    showAlert("Guardando grupo...", "info", loadingAlertId);

    try {

      const response = await fetchApiSchedulesGroupsRegister(userHash, data);

      if (response?.data) {
        setSchedulesGroups(prev => [...response.data, ...prev]);
      }

      replaceAlert(loadingAlertId, "¡Grupo guardado con éxito!", "success");

    } catch (error) {
      replaceAlert(loadingAlertId, "Error al guardar el grupo: " + error.message, "error");
    }
  };

  const scheduleGroupDelete = async (groups) => {

    const loadingAlertId = new Date().getTime();

    showAlert("Eliminando grupo...", "info", loadingAlertId);

    try {
      const response = await fetchApiSchedulesGroupsDelete(userHash, groups);

      if (response?.deletedCount > 0) {
        setSchedulesGroups(prev =>
          prev.filter(
            g => !groups.includes(g.name)
          )
        );
      }

      replaceAlert(loadingAlertId, "¡Grupo eliminado con éxito!", "success");

    } catch (error) {
      replaceAlert(loadingAlertId, "Error al eliminar el grupo: " + error.message, "error");
    }

  };

  const scheduleGroupUpdate = async (data) => {

    const loadingAlertId =
      new Date().getTime();

    showAlert("Actualizando grupo...", "info", loadingAlertId);

    try {
      const response = await fetchApiSchedulesGroupsUpdate(userHash, data);

      if (response?.data) {
        setSchedulesGroups(prev =>
          prev.map(g => {
            const updated = response.data.find(u => u._id === g._id);
            return updated || g;
          })
        );
      }

      replaceAlert(loadingAlertId, "¡Grupo actualizado!", "success");

    } catch (error) {
      replaceAlert(loadingAlertId, "Error al actualizar: " + error.message, "error");
    }

  };

  const renderSection = () => {
    if (!isAuthenticated) return <Login onLoginSuccess={handleLoginSuccess} showAlert={showAlert} isLoading={isLoading} setIsLoading={setIsLoading} />;

    switch (activeSection) {
      case 'Seguimiento':
        return <Tracking
          devices={devices}
          deviceOrientations={deviceOrientations}
          userSchedules={userSchedules}
          userEvents={userEvents}
          userRoute={userRoute}
          locations={locations}
          toleranceTime={toleranceTime}
          compensationMode={compensationMode}
          compensationTime={compensationTime}
          updateDevicesStatusColor={updateDevicesStatusColor}
        />;
      case 'Reportes':
        return <Reports
          devices={devices}
          userSchedules={userSchedules}
          userEvents={userEvents}
          userRoute={userRoute}
          generateReport={generateReport}
          generateOrientationsReport={generateOrientationsReport}
          defaultEventRows={defaultEventRows}
          setDefaultEventRows={setDefaultEventRows}
          rows={rows}
          setRows={setRows}
          showAlert={showAlert}
          toleranceTime={toleranceTime}
          compensationTime={compensationTime}
        />;
      case 'Administrar':
        return <Administration
          devices={devices}
          userGeoTag={userGeoTag}
          userRoute={userRoute}
          geoTags={geoTags}
          geofences={geofences}
          userSchedules={userSchedules}
          userUpdate={userUpdate}
          scheduleRegister={scheduleRegister}
          deleteSchedule={deleteSchedule}
          updateSchedule={updateSchedule}
          showAlert={showAlert}
        />;
      case 'Usuarios':
        return <Users
          allUsers={allUsers}
          userUpdate={userUpdate}
          userDelete={userDelete}
          userRegister={userRegister}
        />;
      case 'Horarios':
        return <Schedules
          allUsers={allUsers}
          allSchedules={allSchedules}
          userUpdate={userUpdate}
          updateSchedule={updateSchedule}
          scheduleRegister={scheduleRegister}
          deleteSchedule={deleteSchedule}
          showAlert={showAlert}
        />;
      case 'Configuración':
        return <Settings
          toleranceTime={toleranceTime}
          userUpdate={userUpdate}
          periodicGroups={periodicGroups}
          compensationMode={compensationMode}
          compensationTime={compensationTime}
          schedulesGroups={schedulesGroups}
          userSchedules={userSchedules}
          devices={devices}
          scheduleRegister={scheduleRegister}
          scheduleGroupRegister={scheduleGroupRegister}
          scheduleGroupDelete={scheduleGroupDelete}
          scheduleGroupUpdate={scheduleGroupUpdate}
          updateSchedule={updateSchedule}
        />;
      default:
        return <div>Sección no encontrada. Redirigiendo a la sección predeterminada...</div>;
    }
  };

  // Función para agregar una alerta
  const showAlert = (message, type = 'success', id = null) => {
    const newAlert = {
      id: id || crypto.randomUUID(), // Usamos el timestamp como ID único
      message,
      type,
      isLeaving: false, // Nueva propiedad para manejar si la alerta está siendo eliminada
    };

    setAlerts((prevAlerts) => [...prevAlerts, newAlert]);

    if (type !== 'info') {
      // Eliminar la alerta después de 5 segundos
      setTimeout(() => {
        // Marcar la alerta como en proceso de salida (animación)
        setAlerts((prevAlerts) =>
          prevAlerts.map((alert) =>
            alert.id === newAlert.id ? { ...alert, isLeaving: true } : alert
          )
        );

        // Eliminar la alerta completamente después de la animación de salida
        setTimeout(() => {
          closeAlert(newAlert.id);
        }, 500); // Ajustamos el tiempo para que coincida con la animación
      }, 4000);
    }
  };

  const replaceAlert = (id, newMessage, newType) => {
    setAlerts((prevAlerts) =>
      prevAlerts.map((alert) =>
        alert.id === id
          ? { ...alert, message: newMessage, type: newType }
          : alert
      )
    );

    // Iniciar el temporizador para eliminar la alerta después de 5 segundos
    setTimeout(() => {
      // Marcar la alerta como en proceso de salida (animación)
      setAlerts((prevAlerts) =>
        prevAlerts.map((alert) =>
          alert.id === id ? { ...alert, isLeaving: true } : alert
        )
      );

      // Eliminar completamente la alerta después de la animación
      setTimeout(() => {
        closeAlert(id);
      }, 500); // Ajustamos el tiempo para que coincida con la animación
    }, 4000);

  };


  const closeAlert = (id) => {
    setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id));
  };

  const TrackingPage = memo((props) => (
    <Tracking {...props} />
  ));

  const ReportsPage = memo((props) => (
    <Reports {...props} />
  ));

  const AdministrationPage = memo((props) => (
    <Administration {...props} />
  ));

  const UsersPage = memo((props) => (
    <Users {...props} />
  ));

  const SchedulesPage = memo((props) => (
    <Schedules {...props} />
  ));


  return (
    <div className="App" style={{ height: '100vh' }}>
      <div className="alerts_container">
        {alerts.map((alert) => (
          <div key={alert.id} className={`alert-item ${alert.isLeaving ? 'leaving' : ''}`}>
            <Alert
              onClose={() => closeAlert(alert.id)}
              severity={alert.type}
              sx={{
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center', // Centra ícono verticalmente
                position: 'relative', // Permite posicionar la barra
                paddingLeft: '16px', // Espacio para la barra
                borderRadius: '5px',
                overflow: 'hidden',
                width: 'min(550px, 85vw);',
                animation: alert.isLeaving ? 'slide-out-left 0.5s forwards' : 'slide-in-right 0.5s forwards', // Animación basada en el estado
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '6px', // Grosor de la barra
                  backgroundColor: alert.type === 'success' ? '#2e7d32' : alert.type === 'error' ? '#d32f2f' : alert.type === 'info' ? '#0288d1' : '#ec6c01',
                },
                '& .MuiAlert-icon': {
                  alignSelf: 'center', // Centra específicamente el ícono
                  fontSize: '2rem', // Ajusta el tamaño del ícono
                },
                '& .MuiAlertTitle-root': {
                  fontWeight: 'bold', // Hace el título en negritas
                  marginBottom: '4px', // Da un pequeño espacio
                },
              }}
            >
              <AlertTitle>{alert.type === 'success' ? 'Éxito' : alert.type === 'error' ? 'Error' : alert.type === 'info' ? 'Procesando la solicitud...' : 'Advertencia'}</AlertTitle>
              {alert.message}
            </Alert>
          </div>
        ))}
      </div>
      {
        isLoading ?
          <div className="loading_overlay">
            <img src={loaderAnimated} alt='Cargando' style={{ height: '6rem' }}></img>
          </div>
          :
          !isAuthenticated ? (
            <>
              <Routes>
                <Route path="/login" element={
                  <>
                    <ExpirationModal isOpen={isOpenExpModal} onClose={() => setIsOpenExpModal(false)} />
                    <BlockModal isOpen={isOpenBlockModal} onClose={() => setIsOpenBlockModal(false)} />
                    <Login onLoginSuccess={handleLoginSuccess} showAlert={showAlert} isLoading={isLoading} setIsLoading={setIsLoading} />
                  </>
                } />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </>
          ) : (
            <>
              <Header activeSection={activeSection} menuCollapsed={setIsMenuCollapsed} showMovilMenu={showMovilMenu} setShowMovilMenu={setShowMovilMenu} userInfo={userInfo} isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} apiUserInfo={apiUserInfo} />
              <div className='container'>
                <SideNav
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  onLogout={handleLogout}
                  setActiveSection={handleSectionChange}
                  isMenuCollapsed={isMenuCollapsed}
                  showMovilMenu={showMovilMenu}
                  setShowMovilMenu={setShowMovilMenu}
                  userUpdate={userUpdate}
                  apiUserInfo={apiUserInfo}
                  setApiUserInfo={setApiUserInfo}
                  userRoute={userRoute}
                  devices={devices}
                  userSchedules={userSchedules}
                  geoTags={geoTags}
                  geofences={geofences}
                  scheduleRegister={scheduleRegister}
                  showAlert={showAlert}
                  updateSchedule={updateSchedule}
                />
                <div className='princ_container'>
                  <main>
                    {renderSection()}
                  </main>
                  <Footer />
                </div>
              </div>
            </>
          )}
    </div>
  );
}

export default App;
